<script>
import parseDate from 'date-fns/parse'
import lazyLoadComponent from '@router/lazyload-component'
import { couponComputed } from '@state/helpers'

import Layout from '@layouts/main'
import BaseButtonDialog from '@components/BaseButtonDialog'

import { generateUniqueString } from '@utils'
import {
  defaultStatusFilter,
  numberUnitFilter,
} from '@utils/filter-value-to-text'
import { formatDateRange } from '@utils/format-date'
import pageMixin from '@src/mixins/pagination'

export default {
  page: {
    title: '優惠券設定',
    meta: [{ name: 'description', content: 'Coupon List' }],
  },
  components: {
    Layout,
    BaseButtonDialog,
    BaseStateLabel: () =>
      lazyLoadComponent(import('@components/BaseStateLabel')),
  },
  filters: {
    defaultStatusFilter,
    numberUnitFilter,
    formatDateRange,
  },
  mixins: [pageMixin],
  data() {
    return {
      loadedCoupons: [],
      selectedCoupons: [],
      filterData: {
        state: '',
        startDate: '',
        endDate: '',
        type: '',
        inProgress: '',
      },
    }
  },
  computed: {
    ...couponComputed,
    enabledBulkButton() {
      return this.selectedCoupons.length === 0
    },
    enabledCopyButton() {
      return this.selectedCoupons.length === 1
    },
    filterDateRange: {
      get() {
        return [this.filterData.startDate, this.filterData.endDate]
      },
      set(value) {
        const [start, end] = value || Array.from({ length: 2 }, () => '')
        this.filterData.startDate = start
        this.filterData.endDate = end
      },
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToSearchCoupon',
      immediate: true,
    },
  },
  methods: {
    async tryToSearchCoupon() {
      const { items, total } = await this.$store.dispatch(
        'coupon/fetchList',
        Object.assign(this.filterData, this.pageQuery)
      )
      this.loadedCoupons = items
      this.$nextTick(() => {
        this.$refs.pagination.updateTotal(total)
      })
    },
    handleResetSearch() {
      this.filterDateRange = ['', '']
      this.tryToSearchCoupon()
    },
    checkAvailability(row) {
      const now = new Date()
      const { state, startDate, endDate } = row
      if (state === 0) return false

      if (startDate && endDate)
        return now >= parseDate(startDate) && now <= parseDate(endDate)
      if (!startDate) return now <= parseDate(endDate)
      if (!endDate) return now >= parseDate(startDate)
      return state
    },
    goToViewCoupon(row) {
      const couponId = row.id
      this.$router.push({ name: 'view-coupon', params: { couponId } })
    },
    goToAddCoupon() {
      this.$router.push({ name: 'add-coupon' })
    },
    async handleBulkUpdateState(state) {
      const couponIds = this.selectedCoupons.map(({ id }) => id)
      await this.$store.dispatch('coupon/updateState', {
        ids: couponIds,
        state,
      })
      this.tryToSearchCoupon()
    },
    async handleDeleteCoupon() {
      const couponIds = this.selectedCoupons.map(({ id }) => id)
      await this.$store.dispatch('coupon/remove', { ids: couponIds })
      this.tryToSearchCoupon()
    },
    async handleDuplicateCoupon() {
      const [couponId] = this.selectedCoupons.map(({ id }) => id)

      const { id, code, ...couponData } = await this.$store.dispatch(
        'coupon/fetchSingle',
        couponId
      )
      const randomCode = `${
        this.currentUser.merchantAlias
      }_${generateUniqueString(5)}`

      const duplicateData = {
        ...couponData,
        code: randomCode.toUpperCase(),
      }

      await this.$store.dispatch('coupon/store', duplicateData)
      this.tryToSearchCoupon()
    },
    handleSelectionChange(selected) {
      this.selectedCoupons = selected
    },
  },
}
</script>

<template>
  <Layout>
    <BasePageHeader>
      <template v-slot:title>優惠券管理</template>
      <template v-slot:right>
        <el-button :disabled="enabledBulkButton" @click="handleBulkUpdateState(1)">
          <BaseIcon name="eye" />
          <span>變更為開放</span>
        </el-button>
        <el-button :disabled="enabledBulkButton" @click="handleBulkUpdateState(0)">
          <BaseIcon name="eye-slash" />
          <span>變更為不開放</span>
        </el-button>
        <el-button type="danger" :disabled="enabledBulkButton" @click="handleDeleteCoupon">
          <BaseIcon name="trash-alt" />
          <span>刪除選取</span>
        </el-button>
        <el-button :disabled="!enabledCopyButton" @click="handleDuplicateCoupon">
          <BaseIcon name="copy" />
          <span>複製</span>
        </el-button>
      </template>
    </BasePageHeader>
    <BaseTableSearch v-slot="{ fields }" :default-value="filterData" @reset="handleResetSearch">
      <el-form-item prop="state">
        <el-select v-model="fields.state" placeholder="選擇開放狀態" @change="tryToSearchCoupon">
          <el-option label="開放中" value="1"></el-option>
          <el-option label="未開放" value="0"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-date-picker
          v-model="filterDateRange"
          type="daterange"
          align="right"
          value-format="yyyy-MM-dd"
          range-separator="-"
          start-placeholder="起始日期"
          end-placeholder="結束日期"
          :clearable="false"
          unlink-panels
          @change="tryToSearchCoupon"
        ></el-date-picker>
      </el-form-item>
      <el-form-item prop="inProgress">
        <el-select v-model="fields.inProgress" placeholder="目前可使用" @change="tryToSearchCoupon">
          <el-option label="可使用" value="1"></el-option>
          <el-option label="不可使用" value="0"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="type">
        <el-select v-model="fields.type" placeholder="折扣類型" @change="tryToSearchCoupon">
          <el-option label="固定金額（元）" value="fixed"></el-option>
          <el-option label="按訂單總額比例（％）" value="%"></el-option>
        </el-select>
      </el-form-item>
    </BaseTableSearch>
    <el-table
      :data="loadedCoupons"
      :class="$style['table']"
      @row-click.once="goToViewCoupon"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55"></el-table-column>
      <el-table-column v-slot="{ row }" label="優惠券名稱／代號">
        <b>{{ row.title }}</b>
        <div>{{ row.code }}</div>
      </el-table-column>
      <el-table-column v-slot="{ row }" label="目前可使用" width="100" align="center">
        <BaseIcon v-if="checkAvailability(row)" name="check" class="only-icon" />
      </el-table-column>
      <el-table-column v-slot="{ row }" label="開放狀態／可使用日期">
        <BaseStateLabel
          :state="row.state | defaultStatusFilter('color') "
          text
        >{{ row.state | defaultStatusFilter('text') }}</BaseStateLabel>
        <div>{{ row.startDate | formatDateRange(row.endDate) }}</div>
      </el-table-column>
      <el-table-column
        v-slot="{ row }"
        label="折扣"
        width="100"
      >-{{ row.discount }}{{ row.type | numberUnitFilter }}</el-table-column>
      <el-table-column
        v-slot="{ row }"
        label="已被使用／發放總張數"
      >{{ `${row.used}張／${(row.infinity === 1) ? '無上限' : `${row.limit}張` }` }}</el-table-column>
      <el-table-column width="50">
        <el-tooltip content="查看優惠券詳情" placement="top">
          <el-button type="text">
            <BaseIcon name="ellipsis-v" />
          </el-button>
        </el-tooltip>
      </el-table-column>
    </el-table>
    <BasePagination ref="pagination" :current-page.sync="currentPage" />
    <BaseButtonDialog button-tooltip="新增優惠券" only-button floating @opened="goToAddCoupon" />
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.table {
  @include layer-elevation(5);
}
</style>
